/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	Device Registration Modal Component
 **	Simplified registration flow for mobile devices with QR scanning
 **
 **	Author(s): Claude Code Assistant
 */

import React, { useState, useEffect, useRef } from 'react'
import { IOSSlideUpPanel } from './ui/IOSSlideUpPanel'
import { MaterialIcon } from './ui/MaterialIcon'
import { OCAColors, Typography, Spacing } from '../theme/colors'
import { BarcodeScanner, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning'
import type { BarcodesScannedEvent } from '@capacitor-mlkit/barcode-scanning'
import type { DeviceCreateUpdate } from '@silverfallscapital/strata47-typescript-sdk'
import { apiTokenConfiguration } from '../services/apiConfig'
import { useDataStore } from '../store/dataStore'
import { buildLocationHierarchy } from '../utils/locationHierarchy'
import type { Customer } from '../types/locations'
import { HierarchicalLocationPicker } from './HierarchicalLocationPicker'
import { getDeviceIdBySerial, updateDeviceById } from '../services/api'
import Config from '../services/config'

// Feature flag for serial number registration
const USE_SERIAL_REGISTRATION = Config.STRATA47_USE_SERIAL_REGISTRATION === 'true'

// Serial number validation regex (G- followed by 7 digits)
const SERIAL_REGEX = /^G-\d{7}$/

interface DeviceRegistrationModalProps {
  isOpen: boolean
  onClose: () => void
  accessToken: string
  namespaceId: string
  onDeviceRegistered?: (deviceId: string) => void
}

interface RegistrationState {
  deviceId?: string
  deviceCode?: string
  serialNumber?: string
  deviceTypeId?: string
  deviceName: string
  selectedLocationId?: string
  selectedLocationName?: string
  selectedLocationPath?: string
  warningThreshold: string
  alarmThreshold: string
  isExistingDevice?: boolean  // Track if device already exists to show info message
}

type RegistrationStep = 'scan' | 'configure' | 'complete'

// UUIDv7 validation regex
const UUIDV7_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// Helper function to transfer device to user's namespace
async function transferDeviceToNamespace(
  deviceId: string,
  targetNamespaceId: string,
  deviceName: string,
  deviceTypeId: string,
  locationId: string | undefined,
  accessToken: string
): Promise<{ success: boolean; previousNamespace?: string; newNamespace?: string; error?: string }> {
  try {
    // Get auth configuration from the SDK pattern
    const config = apiTokenConfiguration(accessToken)
    const tokenValue = accessToken.startsWith('auth0|') ? accessToken.split('|')[1] : accessToken.replace('strata47|', '')

    const requestBody = {
      targetNamespaceId,
      name: deviceName,
      deviceTypeId,
      adminState: true,
      ...(locationId && { locationId })
    }

    console.log('🔄 Transferring device to namespace:', {
      deviceId,
      targetNamespaceId,
      name: deviceName,
      locationId
    })

    const response = await fetch(`${Config.STRATA47_API_URL}/device/${deviceId}/namespace`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenValue}`,
        'x-bp-token': config.headers?.['x-bp-token'] as string || '',
        'X-Client-Type': 'omniiq-mobile-client',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Namespace transfer failed:', response.status, errorText)
      return { success: false, error: `Transfer failed: ${response.status}` }
    }

    const result = await response.json()

    console.log('✅ Device transferred successfully:', {
      deviceId: result.deviceId,
      previousNamespace: result.previousNamespace,
      newNamespace: result.newNamespace,
      created: result.created
    })

    return {
      success: true,
      previousNamespace: result.previousNamespace,
      newNamespace: result.newNamespace
    }
  } catch (error) {
    console.error('Failed to transfer device to namespace:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' }
  }
}

export const DeviceRegistrationModal: React.FC<DeviceRegistrationModalProps> = ({
  isOpen,
  onClose,
  accessToken,
  namespaceId,
  onDeviceRegistered
}) => {
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('scan')
  const [registrationState, setRegistrationState] = useState<RegistrationState>({
    deviceName: '',
    warningThreshold: '0.02',
    alarmThreshold: '0.01'
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [manualCodeEntry, setManualCodeEntry] = useState(false)
  const [showLocationPicker, setShowLocationPicker] = useState(false)

  const { locations, fetchLocations } = useDataStore()
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([])
  const deviceNameInputRef = useRef<HTMLInputElement | null>(null)
  const [locationHierarchy, setLocationHierarchy] = useState<Customer[]>([])
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(new Set())

  // Load locations when modal opens
  useEffect(() => {
    if (isOpen && accessToken) {
      fetchLocations(accessToken)
    }
  }, [isOpen, accessToken, fetchLocations])

  // Build location hierarchy when locations change
  useEffect(() => {
    if (locations.length > 0) {
      const hierarchy = buildLocationHierarchy(locations)
      setLocationHierarchy(hierarchy.customers)
    }
  }, [locations])

  // Auto-focus first code input when switching to manual entry
  useEffect(() => {
    if (manualCodeEntry && codeInputRefs.current[0]) {
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100)
    }
  }, [manualCodeEntry])

  // Auto-focus device name when entering configure step
  useEffect(() => {
    if (currentStep === 'configure' && deviceNameInputRef.current) {
      setTimeout(() => deviceNameInputRef.current?.focus(), 100)
    }
  }, [currentStep])

  // Validate UUIDv7
  const isValidUUIDv7 = (uuid: string): boolean => {
    return UUIDV7_REGEX.test(uuid)
  }

  // Parse QR code data (supports JSON with deviceId/deviceTypeId, plain UUIDv7, or serial number)
  const parseQRCodeData = (scannedValue: string): { deviceId?: string; serialNumber?: string; deviceTypeId?: string } | null => {
    // If using serial registration, check for serial format first
    if (USE_SERIAL_REGISTRATION && SERIAL_REGEX.test(scannedValue)) {
      return { serialNumber: scannedValue }
    }

    try {
      // Try parsing as JSON first
      const parsed = JSON.parse(scannedValue)

      // Check for serial number in JSON
      if (USE_SERIAL_REGISTRATION && parsed.serialNumber && SERIAL_REGEX.test(parsed.serialNumber)) {
        return {
          serialNumber: parsed.serialNumber,
          deviceTypeId: parsed.deviceTypeId
        }
      }

      // Validate required deviceId field
      if (!parsed.deviceId || !isValidUUIDv7(parsed.deviceId)) {
        return null
      }

      // Validate optional deviceTypeId if provided
      if (parsed.deviceTypeId && !isValidUUIDv7(parsed.deviceTypeId)) {
        return null
      }

      return {
        deviceId: parsed.deviceId,
        deviceTypeId: parsed.deviceTypeId
      }
    } catch {
      // If not valid JSON, try as plain UUIDv7 string (legacy format)
      if (isValidUUIDv7(scannedValue)) {
        return { deviceId: scannedValue }
      }
      return null
    }
  }

  // Handle QR code scan
  const handleScanQR = async () => {
    try {
      setScanning(true)
      setError(null)

      // Check camera permission
      const permission = await BarcodeScanner.checkPermissions()
      if (permission.camera !== 'granted') {
        const request = await BarcodeScanner.requestPermissions()
        if (request.camera !== 'granted') {
          setError('Camera permission is required to scan QR codes')
          setScanning(false)
          return
        }
      }

      // Add class to html and body to show camera
      document.querySelector('html')?.classList.add('barcode-scanner-active')
      document.querySelector('body')?.classList.add('barcode-scanner-active')

      // Listen for scan result
      const listener = await BarcodeScanner.addListener('barcodesScanned', async (event: BarcodesScannedEvent) => {
        if (event.barcodes && event.barcodes.length > 0) {
          const scannedValue = event.barcodes[0].displayValue

          if (!scannedValue) {
            setError('QR code is empty')
            BarcodeScanner.stopScan()
            document.querySelector('html')?.classList.remove('barcode-scanner-active')
            document.querySelector('body')?.classList.remove('barcode-scanner-active')
            listener.remove()
            setScanning(false)
            return
          }

          // Parse QR code data
          const parsedData = parseQRCodeData(scannedValue)

          if (parsedData) {
            console.log('✅ QR code parsed successfully:', parsedData)

            // If serial number registration mode, lookup deviceId
            if (USE_SERIAL_REGISTRATION && parsedData.serialNumber) {
              setLoading(true)
              const deviceId = await getDeviceIdBySerial(parsedData.serialNumber, accessToken)
              setLoading(false)

              if (!deviceId) {
                setError(`Device not found for serial: ${parsedData.serialNumber}`)
                BarcodeScanner.stopScan()
                document.querySelector('html')?.classList.remove('barcode-scanner-active')
                document.querySelector('body')?.classList.remove('barcode-scanner-active')
                listener.remove()
                setScanning(false)
                return
              }

              // Check if we already have this device from the store and pre-populate fields
              const { devices } = useDataStore.getState()
              const existingDevice = devices.find((d: any) => d.deviceId === deviceId)
              if (existingDevice) {
                console.log('✅ Found existing device, pre-populating fields:', existingDevice)
                setRegistrationState(prev => ({
                  ...prev,
                  serialNumber: parsedData.serialNumber,
                  deviceId: deviceId,
                  deviceTypeId: existingDevice.deviceTypeId || parsedData.deviceTypeId || Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23',
                  deviceName: existingDevice.name || '',
                  selectedLocationId: existingDevice.locationId,
                  selectedLocationName: locations.find((l: any) => l.locationId === existingDevice.locationId)?.name,
                  isExistingDevice: true
                }))
              } else {
                console.log('📝 New device registration')
                setRegistrationState(prev => ({
                  ...prev,
                  serialNumber: parsedData.serialNumber,
                  deviceId: deviceId,
                  deviceTypeId: parsedData.deviceTypeId || Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23',
                  isExistingDevice: false
                }))
              }
            } else {
              // Standard deviceId-based registration
              setRegistrationState(prev => ({
                ...prev,
                deviceId: parsedData.deviceId,
                deviceTypeId: parsedData.deviceTypeId || Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23'
              }))
            }

            // Clear error and advance to configuration step
            setError(null)
            setLoading(false)
            setCurrentStep('configure')
          } else {
            if (USE_SERIAL_REGISTRATION) {
              setError('Invalid QR code format. Expected serial number (G-XXXXXXX)')
            } else {
              setError('Invalid QR code format. Expected JSON: {"deviceId": "...", "deviceTypeId": "..."} or plain UUIDv7')
            }
          }

          // Stop scanning and remove class
          BarcodeScanner.stopScan()
          document.querySelector('html')?.classList.remove('barcode-scanner-active')
          document.querySelector('body')?.classList.remove('barcode-scanner-active')
          listener.remove()
          setScanning(false)
        }
      })

      // Start scanning
      await BarcodeScanner.startScan({
        formats: [BarcodeFormat.QrCode]
      })
    } catch (err) {
      console.error('Failed to scan QR code:', err)
      setError('Failed to scan QR code. Please try manual entry.')
      document.querySelector('html')?.classList.remove('barcode-scanner-active')
      document.querySelector('body')?.classList.remove('barcode-scanner-active')
      setScanning(false)
    }
  }

  // Handle cancel scanning
  const handleCancelScan = async () => {
    await BarcodeScanner.stopScan()
    document.querySelector('html')?.classList.remove('barcode-scanner-active')
    document.querySelector('body')?.classList.remove('barcode-scanner-active')
    setScanning(false)
  }

  // Handle manual code entry (8-digit code)
  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const currentCode = registrationState.deviceCode || ''
      const codeArray = currentCode.padEnd(8, ' ').split('')
      codeArray[index] = value
      const newCode = codeArray.join('').replace(/ /g, '')

      setRegistrationState(prev => ({ ...prev, deviceCode: newCode }))

      // Auto-focus next input
      if (value && index < 7) {
        codeInputRefs.current[index + 1]?.focus()
      }

      // Auto-verify when 8 digits entered
      if (newCode.length === 8) {
        setTimeout(() => handleManualCodeSubmit(newCode), 300)
      }
    }
  }

  // Handle manual code submission (mock verification)
  const handleManualCodeSubmit = async (code: string) => {
    setLoading(true)
    setError(null)

    try {
      // Mock API call to verify device code
      // TODO: Replace with actual Strata47 API endpoint when available
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Mock device ID and device type ID generation
      const mockDeviceId = `01234567-89ab-7def-8012-${code.substring(0, 8).padEnd(8, '0')}`
      const mockDeviceTypeId = Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23'

      setRegistrationState(prev => ({
        ...prev,
        deviceId: mockDeviceId,
        deviceTypeId: mockDeviceTypeId
      }))
      setCurrentStep('configure')
    } catch (err) {
      setError('Failed to verify device code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle serial number submission
  const handleSerialSubmit = async () => {
    const serialNumber = registrationState.serialNumber?.trim()

    if (!serialNumber) {
      setError('Please enter a serial number')
      return
    }

    // Validate serial number format
    if (!SERIAL_REGEX.test(serialNumber)) {
      setError('Invalid serial number format. Expected: G-XXXXXXX (G- followed by 7 digits)')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Lookup deviceId by serial number
      const deviceId = await getDeviceIdBySerial(serialNumber, accessToken)

      if (!deviceId) {
        setError(`Device not found for serial: ${serialNumber}`)
        setLoading(false)
        return
      }

      console.log('✅ Serial number verified, deviceId:', deviceId)

      // Check if we already have this device from the store and pre-populate fields
      const { devices } = useDataStore.getState()
      const existingDevice = devices.find((d: any) => d.deviceId === deviceId)
      if (existingDevice) {
        console.log('✅ Found existing device, pre-populating fields:', existingDevice)
        setRegistrationState(prev => ({
          ...prev,
          deviceId: deviceId,
          deviceTypeId: existingDevice.deviceTypeId || Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23',
          deviceName: existingDevice.name || '',
          selectedLocationId: existingDevice.locationId,
          selectedLocationName: locations.find((l: any) => l.locationId === existingDevice.locationId)?.name,
          isExistingDevice: true
        }))
      } else {
        console.log('📝 New device registration')
        setRegistrationState(prev => ({
          ...prev,
          deviceId: deviceId,
          deviceTypeId: Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23',
          isExistingDevice: false
        }))
      }
      // Clear error and advance to configuration step
      setError(null)
      setCurrentStep('configure')
    } catch (err) {
      console.error('Failed to verify serial number:', err)
      setError('Failed to verify serial number. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle device registration
  const handleRegisterDevice = async () => {
    if (!registrationState.deviceId || !registrationState.deviceName.trim()) {
      setError('Device name is required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Check if this is an existing device (we detected it during serial lookup)
      if (registrationState.isExistingDevice) {
        // Device already in namespace - just update name/location
        console.log('📝 Updating existing device configuration (already in namespace)')

        const updateData: DeviceCreateUpdate = {
          name: registrationState.deviceName.trim(),
          ...(registrationState.selectedLocationId && {
            locationId: registrationState.selectedLocationId
          })
        }

        await updateDeviceById(
          registrationState.deviceId!,
          updateData,
          accessToken
        )

        console.log('✅ Device updated successfully:', {
          deviceId: registrationState.deviceId,
          name: updateData.name,
          locationId: updateData.locationId
        })
      } else {
        // New device - transfer to user's namespace
        console.log('📝 Transferring device to namespace')

        const result = await transferDeviceToNamespace(
          registrationState.deviceId!,
          namespaceId,
          registrationState.deviceName.trim(),
          registrationState.deviceTypeId || Config.STRATA47_DEFAULT_DEVICE_TYPE_ID || '0195ddab-19d5-7ff0-9d4e-83eb95358b23',
          registrationState.selectedLocationId,
          accessToken
        )

        if (!result.success) {
          throw new Error(result.error || 'Failed to transfer device to namespace')
        }

        console.log('✅ Device transferred successfully:', {
          deviceId: registrationState.deviceId,
          previousNamespace: result.previousNamespace,
          newNamespace: result.newNamespace
        })
      }

      // Registration successful - trigger refresh and complete
      console.log('✅ Device registration complete, refreshing device list')
      if (onDeviceRegistered && registrationState.deviceId) {
        onDeviceRegistered(registrationState.deviceId)
      }

      setCurrentStep('complete')
      setLoading(false)
    } catch (err: any) {
      console.error('Failed to register device:', err)

      // Build detailed error message for debugging on iOS (where console logs aren't accessible)
      let errorMessage = 'Failed to register device. '

      if (err?.message) {
        errorMessage += `Error: ${err.message}. `
      }

      // Add operation context with serial number
      if (registrationState.isExistingDevice) {
        errorMessage += `(Updating existing device ${registrationState.deviceId}`
        if (registrationState.serialNumber) {
          errorMessage += `, serial: ${registrationState.serialNumber}`
        }
        errorMessage += `)`
      } else {
        errorMessage += `(Transferring device ${registrationState.deviceId}`
        if (registrationState.serialNumber) {
          errorMessage += `, serial: ${registrationState.serialNumber}`
        }
        errorMessage += ` to namespace ${namespaceId})`
      }

      // Add error type if available
      if (err?.name && err.name !== 'Error') {
        errorMessage += ` [${err.name}]`
      }

      // Add status code if it's an HTTP error
      if (err?.status) {
        errorMessage += ` Status: ${err.status}`
      }

      setError(errorMessage)
      setLoading(false)
    }
  }

  // Handle toggle location expansion
  const toggleLocationExpansion = (locationId: string) => {
    setExpandedLocations(prev => {
      const newSet = new Set(prev)
      if (newSet.has(locationId)) {
        newSet.delete(locationId)
      } else {
        newSet.add(locationId)
      }
      return newSet
    })
  }

  // Handle location selection
  const handleLocationSelect = (locationId: string, locationName: string, path: string) => {
    setRegistrationState(prev => ({
      ...prev,
      selectedLocationId: locationId,
      selectedLocationName: locationName,
      selectedLocationPath: path
    }))
    setShowLocationPicker(false)
  }

  // Handle close and reset
  const handleClose = () => {
    setCurrentStep('scan')
    setRegistrationState({
      deviceName: '',
      warningThreshold: '0.02',
      alarmThreshold: '0.01'
    })
    setError(null)
    setLoading(false)
    setScanning(false)
    setManualCodeEntry(false)
    setShowLocationPicker(false)
    setExpandedLocations(new Set())
    onClose()
  }

  // Render scan step
  const renderScanStep = () => (
    <div style={{ padding: Spacing.lg }}>
      <div style={{
        textAlign: 'center',
        marginBottom: Spacing.xl
      }}>
        <div style={{
          fontSize: Typography.fontSize.base,
          color: OCAColors.secondaryLabel,
          marginBottom: Spacing.lg
        }}>
          {manualCodeEntry
            ? (USE_SERIAL_REGISTRATION
                ? 'Enter the device serial number (G-XXXXXXX)'
                : 'Enter the 8-digit code displayed on your device')
            : (USE_SERIAL_REGISTRATION
                ? 'Scan the QR code on your device or enter the serial number manually'
                : 'Scan the QR code on your device or enter the registration code manually')
          }
        </div>

        {error && (
          <div style={{
            padding: Spacing.md,
            backgroundColor: OCAColors.danger + '15',
            borderRadius: '8px',
            color: OCAColors.danger,
            fontSize: Typography.fontSize.sm,
            marginBottom: Spacing.lg
          }}>
            {error}
          </div>
        )}
      </div>

      {!manualCodeEntry ? (
        // QR Scanner UI
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleScanQR}
            style={{
              width: '100%',
              padding: Spacing.lg,
              backgroundColor: OCAColors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              cursor: 'pointer',
              marginBottom: Spacing.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: Spacing.sm
            }}
          >
            <MaterialIcon name="qr_code_scanner" size={24} color="white" />
            Scan QR Code
          </button>

          <button
            onClick={() => setManualCodeEntry(true)}
            style={{
              width: '100%',
              padding: Spacing.md,
              backgroundColor: 'transparent',
              color: OCAColors.primary,
              border: 'none',
              fontSize: Typography.fontSize.base,
              cursor: 'pointer'
            }}
          >
            {USE_SERIAL_REGISTRATION ? 'Enter Serial Number Manually' : 'Enter Code Manually'}
          </button>
        </div>
      ) : USE_SERIAL_REGISTRATION ? (
        // Serial number entry UI
        <div>
          <div style={{
            position: 'relative',
            marginBottom: Spacing.lg
          }}>
            <div style={{
              position: 'absolute',
              left: '0',
              top: '0',
              bottom: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              fontSize: Typography.fontSize.xl,
              fontWeight: Typography.fontWeight.bold,
              color: OCAColors.label,
              pointerEvents: 'none',
              letterSpacing: '2px'
            }}>
              G-
            </div>
            <input
              type="text"
              value={(registrationState.serialNumber || '').replace(/^G-/, '')}
              onChange={(e) => {
                const digits = e.target.value.replace(/[^0-9]/g, '').substring(0, 7)
                setRegistrationState(prev => ({ ...prev, serialNumber: `G-${digits}` }))
              }}
              placeholder="0000000"
              maxLength={7}
              style={{
                width: '100%',
                padding: Spacing.lg,
                paddingLeft: '80px',
                fontSize: Typography.fontSize.xl,
                fontWeight: Typography.fontWeight.bold,
                textAlign: 'center',
                border: `2px solid ${OCAColors.separator}`,
                borderRadius: '8px',
                backgroundColor: OCAColors.background,
                color: OCAColors.label,
                letterSpacing: '2px'
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
            />
          </div>

          <button
            onClick={handleSerialSubmit}
            disabled={loading || !registrationState.serialNumber}
            style={{
              width: '100%',
              padding: Spacing.lg,
              backgroundColor: loading || !registrationState.serialNumber
                ? OCAColors.separator
                : OCAColors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              cursor: loading || !registrationState.serialNumber ? 'not-allowed' : 'pointer',
              marginBottom: Spacing.md
            }}
          >
            {loading ? 'Verifying...' : 'Verify Serial Number'}
          </button>

          <button
            onClick={() => setManualCodeEntry(false)}
            style={{
              width: '100%',
              padding: Spacing.md,
              backgroundColor: 'transparent',
              color: OCAColors.primary,
              border: 'none',
              fontSize: Typography.fontSize.base,
              cursor: 'pointer'
            }}
          >
            ← Back to QR Scanner
          </button>
        </div>
      ) : (
        // Manual 8-digit code entry UI
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: Spacing.xl
          }}>
            {Array.from({ length: 8 }).map((_, index) => {
              const codeDigits = (registrationState.deviceCode || '').padEnd(8, ' ').split('')
              const digit = codeDigits[index]

              return (
                <React.Fragment key={`digit-${index}`}>
                  <input
                    ref={(el) => { codeInputRefs.current[index] = el }}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={digit === ' ' ? '' : digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    maxLength={1}
                    style={{
                      width: '40px',
                      height: '50px',
                      textAlign: 'center',
                      fontSize: Typography.fontSize.xl,
                      fontWeight: Typography.fontWeight.bold,
                      border: `2px solid ${digit && digit !== ' ' ? OCAColors.primary : OCAColors.separator}`,
                      borderRadius: '8px',
                      backgroundColor: OCAColors.background,
                      color: OCAColors.label
                    }}
                  />
                  {index === 3 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: Typography.fontSize.xl,
                      fontWeight: Typography.fontWeight.bold,
                      color: OCAColors.secondaryLabel
                    }}>
                      -
                    </div>
                  )}
                </React.Fragment>
              )
            })}
          </div>

          {loading && (
            <div style={{
              textAlign: 'center',
              color: OCAColors.secondaryLabel,
              fontSize: Typography.fontSize.sm
            }}>
              Verifying code...
            </div>
          )}

          <button
            onClick={() => setManualCodeEntry(false)}
            style={{
              width: '100%',
              padding: Spacing.md,
              backgroundColor: 'transparent',
              color: OCAColors.primary,
              border: 'none',
              fontSize: Typography.fontSize.base,
              cursor: 'pointer',
              marginTop: Spacing.md
            }}
          >
            ← Back to QR Scanner
          </button>
        </div>
      )}
    </div>
  )

  // Render configure step
  const renderConfigureStep = () => (
    <div style={{ padding: Spacing.lg }}>
      {/* Info message for existing devices */}
      {registrationState.isExistingDevice && (
        <div style={{
          padding: Spacing.md,
          backgroundColor: OCAColors.primary + '15',
          borderRadius: '8px',
          color: OCAColors.primary,
          fontSize: Typography.fontSize.sm,
          marginBottom: Spacing.lg,
          display: 'flex',
          alignItems: 'flex-start',
          gap: Spacing.sm
        }}>
          <MaterialIcon name="info" size={20} color={OCAColors.primary} />
          <span>Device already registered. Update configuration:</span>
        </div>
      )}

      {error && (
        <div style={{
          padding: Spacing.md,
          backgroundColor: OCAColors.danger + '15',
          borderRadius: '8px',
          color: OCAColors.danger,
          fontSize: Typography.fontSize.sm,
          marginBottom: Spacing.lg
        }}>
          {error}
        </div>
      )}

      {/* Device Name */}
      <div style={{ marginBottom: Spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          marginBottom: Spacing.xs
        }}>
          Device Name *
        </label>
        <input
          ref={deviceNameInputRef}
          type="text"
          value={registrationState.deviceName}
          onChange={(e) => setRegistrationState(prev => ({ ...prev, deviceName: e.target.value }))}
          placeholder="e.g., Main Entrance Monitor"
          style={{
            width: '100%',
            padding: Spacing.md,
            fontSize: Typography.fontSize.base,
            border: `1px solid ${OCAColors.separator}`,
            borderRadius: '8px',
            backgroundColor: OCAColors.background,
            color: OCAColors.label
          }}
        />
      </div>

      {/* Location Picker */}
      <div style={{ marginBottom: Spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          marginBottom: Spacing.xs
        }}>
          Location (Optional)
        </label>
        <button
          onClick={() => setShowLocationPicker(!showLocationPicker)}
          style={{
            width: '100%',
            padding: Spacing.md,
            fontSize: Typography.fontSize.base,
            border: `1px solid ${OCAColors.separator}`,
            borderRadius: '8px',
            backgroundColor: OCAColors.background,
            color: registrationState.selectedLocationName ? OCAColors.label : OCAColors.secondaryLabel,
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>{registrationState.selectedLocationName || 'Select location'}</span>
          <MaterialIcon
            name={showLocationPicker ? 'expand_less' : 'expand_more'}
            size={20}
            color={OCAColors.secondaryLabel}
          />
        </button>

        {showLocationPicker && (
          <HierarchicalLocationPicker
            customers={locationHierarchy}
            expandedLocations={expandedLocations}
            selectedLocationId={registrationState.selectedLocationId}
            onToggleExpansion={toggleLocationExpansion}
            onSelectLocation={handleLocationSelect}
          />
        )}
      </div>

      {/* Warning Threshold */}
      <div style={{ marginBottom: Spacing.lg }}>
        <label style={{
          display: 'block',
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          marginBottom: Spacing.xs
        }}>
          Warning Threshold (inWC)
        </label>
        <input
          type="number"
          step="0.01"
          value={registrationState.warningThreshold}
          onChange={(e) => setRegistrationState(prev => ({ ...prev, warningThreshold: e.target.value }))}
          style={{
            width: '100%',
            padding: Spacing.md,
            fontSize: Typography.fontSize.base,
            border: `1px solid ${OCAColors.separator}`,
            borderRadius: '8px',
            backgroundColor: OCAColors.background,
            color: OCAColors.label
          }}
        />
      </div>

      {/* Alarm Threshold */}
      <div style={{ marginBottom: Spacing.xl }}>
        <label style={{
          display: 'block',
          fontSize: Typography.fontSize.sm,
          fontWeight: Typography.fontWeight.semibold,
          color: OCAColors.label,
          marginBottom: Spacing.xs
        }}>
          Alarm Threshold (inWC)
        </label>
        <input
          type="number"
          step="0.01"
          value={registrationState.alarmThreshold}
          onChange={(e) => setRegistrationState(prev => ({ ...prev, alarmThreshold: e.target.value }))}
          style={{
            width: '100%',
            padding: Spacing.md,
            fontSize: Typography.fontSize.base,
            border: `1px solid ${OCAColors.separator}`,
            borderRadius: '8px',
            backgroundColor: OCAColors.background,
            color: OCAColors.label
          }}
        />
      </div>

      <button
        onClick={handleRegisterDevice}
        disabled={loading || !registrationState.deviceName.trim()}
        style={{
          width: '100%',
          padding: Spacing.lg,
          backgroundColor: loading || !registrationState.deviceName.trim()
            ? OCAColors.separator
            : OCAColors.primary,
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: Typography.fontSize.lg,
          fontWeight: Typography.fontWeight.semibold,
          cursor: loading || !registrationState.deviceName.trim() ? 'not-allowed' : 'pointer'
        }}
      >
        {loading ? 'Registering...' : 'Register Device'}
      </button>
    </div>
  )

  // Render complete step
  const renderCompleteStep = () => (
    <div style={{ padding: Spacing.lg, textAlign: 'center' }}>
      <div style={{
        marginBottom: Spacing.xl,
        fontSize: Typography.fontSize['2xl'],
        color: OCAColors.success
      }}>
        <MaterialIcon name="check_circle" size={64} color={OCAColors.success} />
      </div>

      <div style={{
        fontSize: Typography.fontSize.xl,
        fontWeight: Typography.fontWeight.bold,
        color: OCAColors.label,
        marginBottom: Spacing.md
      }}>
        Registration Complete!
      </div>

      <div style={{
        fontSize: Typography.fontSize.base,
        color: OCAColors.secondaryLabel,
        marginBottom: Spacing.xl
      }}>
        {registrationState.deviceName} has been successfully registered and is ready to use.
      </div>

      <button
        onClick={handleClose}
        style={{
          width: '100%',
          padding: Spacing.lg,
          backgroundColor: OCAColors.primary,
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: Typography.fontSize.lg,
          fontWeight: Typography.fontWeight.semibold,
          cursor: 'pointer'
        }}
      >
        Done
      </button>
    </div>
  )

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'scan':
        return renderScanStep()
      case 'configure':
        return renderConfigureStep()
      case 'complete':
        return renderCompleteStep()
      default:
        return renderScanStep()
    }
  }

  return (
    <>
      {/* Main registration modal - close when scanning to allow camera view */}
      <IOSSlideUpPanel
        isOpen={isOpen && !scanning}
        onClose={handleClose}
        title="Register Device"
        height="85vh"
      >
        {renderCurrentStep()}
      </IOSSlideUpPanel>

      {/* Camera scanning overlay - shown when scanning */}
      {scanning && (
        <div className="barcode-scanner-modal" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: Spacing.xl,
          backgroundColor: 'transparent'
        }}>
          <div style={{
            textAlign: 'center',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: Spacing.lg,
            borderRadius: '12px',
            marginTop: '60px'
          }}>
            <div style={{
              fontSize: Typography.fontSize.xl,
              fontWeight: Typography.fontWeight.bold,
              marginBottom: Spacing.sm
            }}>
              Scan QR Code
            </div>
            <div style={{
              fontSize: Typography.fontSize.base
            }}>
              Position the QR code within the frame
            </div>
          </div>

          <button
            onClick={handleCancelScan}
            style={{
              padding: `${Spacing.md} ${Spacing.xl}`,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: '2px solid white',
              borderRadius: '12px',
              fontSize: Typography.fontSize.lg,
              fontWeight: Typography.fontWeight.semibold,
              cursor: 'pointer',
              marginBottom: '80px'
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  )
}
